import { useState, useMemo, useCallback } from "react";
import { useHR } from "../context/HRContext";
import { useAuthStore } from "@/store/authStore";
import toast from "react-hot-toast";

const today = new Date().toISOString().split("T")[0];

export function useAttendance() {
  const { employees, attendance, leaves, markAttendance, bulkMarkAttendance, isLoading } = useHR();
  const { user } = useAuthStore();

  const [selectedDate, setSelectedDate] = useState(today);
  const [filterEmp, setFilterEmp] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [isMarkOpen, setIsMarkOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    employeeId: "", date: today,
    status: "present",
    checkIn: "", checkOut: "", remarks: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Records for the selected date
  const dayRecords = useMemo(() => {
    const target = selectedDate;
    const attendanceByEmployee = new Map(attendance.filter((a) => {
      if (!a.date) return false;
      const d = new Date(a.date);
      if (isNaN(d.getTime())) return false;
      return d.toISOString().split("T")[0] === target;
    }).map((record) => [record.employeeId, record]));

    leaves.filter((leave) => {
      if (leave.status !== "approved") return false;
      const from = new Date(leave.fromDate).toISOString().split("T")[0];
      const to = new Date(leave.toDate || leave.fromDate).toISOString().split("T")[0];
      return from <= target && to >= target;
    }).forEach((leave) => {
      attendanceByEmployee.set(leave.employeeId, {
        employeeId: leave.employeeId, employeeName: leave.employeeName, date: target,
        status: "on_leave", checkIn: "", checkOut: "", remarks: `${leave.leaveType || "Approved"} leave`,
      });
    });

    return Array.from(attendanceByEmployee.values());
  }, [attendance, leaves, selectedDate]);

  // Employees with their attendance for the selected date
  const employeeAttendance = useMemo(() => {
    return employees.map((emp) => {
      const rec = dayRecords.find((a) => a.employeeId === emp.id);
      return { ...emp, attendance: rec || null };
    });
  }, [employees, dayRecords]);

  const filtered = useMemo(() => {
    return dayRecords.filter((rec) => {
      const q = filterEmp.toLowerCase().trim();
      const matchEmp = !q || rec.employeeName?.toLowerCase().includes(q) || rec.employeeId?.toLowerCase().includes(q);
      
      const recStatus = rec.status || (rec.checkIn ? "present" : "absent");
      const matchStatus = !filterStatus || recStatus === filterStatus;
      
      return matchEmp && matchStatus;
    });
  }, [dayRecords, filterEmp, filterStatus]);

  const stats = useMemo(() => {
    const recs = dayRecords;
    const present = recs.filter((r) => r.status === "present" || r.status === "late" || (!r.status && r.checkIn)).length;
    const absent = recs.filter((r) => r.status === "absent" || (!r.status && !r.checkIn)).length;
    const halfDay = recs.filter((r) => r.status === "half_day").length;
    const onLeave = recs.filter((r) => r.status === "on_leave").length;
    return {
      total: employees.length,
      marked: recs.length,
      present,
      absent,
      halfDay,
      onLeave,
    };
  }, [dayRecords, employees]);

  const openMark = useCallback((emp = null, existing = null) => {
    if (existing) {
      setEditingRecord(existing);
      setFormData({ ...existing });
    } else {
      const defaultEmp = emp || employees.find((e) => e.id === user?.employeeId) || null;
      setEditingRecord(null);
      setFormData({
        employeeId: defaultEmp?.id || "",
        employeeName: defaultEmp?.name || "",
        date: selectedDate,
        status: "present",
        checkIn: "", checkOut: "", remarks: "",
      });
    }
    setFormErrors({});
    setIsMarkOpen(true);
  }, [selectedDate]);

  const closeMark = useCallback(() => {
    setIsMarkOpen(false);
    setEditingRecord(null);
    setFormErrors({});
  }, []);

  const updateField = useCallback((field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    setFormErrors((p) => ({ ...p, [field]: undefined }));
  }, []);

  const validate = (data) => {
    const errs = {};
    if (!data.employeeId) errs.employeeId = "Employee is required";
    if (!data.date) errs.date = "Date is required";
    return errs;
  };

const save = useCallback(async () => {
    const errs = validate(formData);
    if (Object.keys(errs).length) { setFormErrors(errs); return false; }

    const emp = employees.find((e) => e.id === formData.employeeId);
    if (emp?.status?.toLowerCase() === "inactive") {
      toast.error("This employee is inactive. Cannot mark attendance.");
      return false;
    }

    const targetDate = formData.date || today;
    const isOnLeave = leaves.some((l) =>
      l.status === "approved" &&
      l.employeeId === formData.employeeId &&
      new Date(l.fromDate) <= new Date(targetDate) &&
      new Date(l.toDate || l.fromDate) >= new Date(targetDate)
    );
    if (isOnLeave) {
      toast.error("This employee is on approved leave. Cannot mark attendance.");
      return false;
    }

    setIsSaving(true);
    try {
      await new Promise((r) => setTimeout(r, 300));
      await markAttendance({ ...formData, employeeName: emp?.name || formData.employeeName });
      toast.success("Attendance marked successfully");
      closeMark();
      return true;
    } catch (e) {
      toast.error("Failed to mark attendance");
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [formData, markAttendance, employees, leaves, closeMark]);

const saveAll = useCallback(async (records) => {
    setIsSaving(true);
    try {
      const inactiveEmployees = records.filter(r => {
        const emp = employees.find(e => e.id === r.employeeId);
        return emp?.status?.toLowerCase() === "inactive";
      });
      if (inactiveEmployees.length > 0) {
        const names = inactiveEmployees.map(r => r.employeeName || r.employeeId).join(", ");
        toast.error(`Cannot mark attendance for inactive employees: ${names}`);
        setIsSaving(false);
        return null;
      }

      const onLeaveIds = new Set(
        leaves
          .filter((l) => l.status === "approved")
          .filter((l) => {
            const rec = records.find((r) => r.employeeId === l.employeeId);
            if (!rec) return false;
            return new Date(l.fromDate) <= new Date(rec.date) && new Date(l.toDate || l.fromDate) >= new Date(rec.date);
          })
          .map((l) => l.employeeId)
      );
      const blocked = records.filter((r) => onLeaveIds.has(r.employeeId));
      if (blocked.length > 0) {
        const names = blocked.map((r) => r.employeeName || r.employeeId).join(", ");
        toast.error(`Cannot mark attendance for employees on leave: ${names}`);
        setIsSaving(false);
        return null;
      }

      await new Promise((r) => setTimeout(r, 400));
      const result = await bulkMarkAttendance(records);
      setIsBulkOpen(false);
      return result;
    } finally {
      setIsSaving(false);
    }
  }, [bulkMarkAttendance, leaves, employees]);

return {
    selectedDate, setSelectedDate,
    filterEmp, setFilterEmp,
    filterStatus, setFilterStatus,
    filtered, stats, employees, dayRecords, employeeAttendance,
    isMarkOpen, isBulkOpen, setIsBulkOpen,
    editingRecord, formData, formErrors, isSaving, isLoading,
    openMark, closeMark, updateField, save, saveAll,
  };
}

  
